import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Flight as FlightIcon,
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const positionFields = [
  { name: 'p96', label: 'Pos 96"', description: 'Posiciones de 96 pulgadas' },
  { name: 'p118', label: 'Pos 118"', description: 'Posiciones de 118 pulgadas' },
  { name: 'p64', label: 'Pos 64"', description: 'Posiciones de 64 pulgadas' },
  { name: 'spec', label: 'SPEC', description: 'Posiciones especiales' },
  { name: 'bulk', label: 'BULK', description: 'Carga suelta' },
  { name: 'pyb', label: 'PYB', description: 'Posiciones PYB' },
  { name: 'pa', label: 'A', description: 'Posiciones A' },
  { name: 'pqa', label: 'PQA', description: 'Posiciones PQA' },
];

const Equipos = () => {
  const { isAdmin } = useAuth();
  const [equipos, setEquipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // 'create' | 'edit'
  const [selectedEquipo, setSelectedEquipo] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    p96: 0,
    p118: 0,
    p64: 0,
    spec: 0,
    bulk: 0,
    pyb: 0,
    pa: 0,
    pqa: 0,
    tpos: 0,
    capcarga: 0,
    maxload: 0,
    maxcol: 13,
    nmfila: 0,
  });

  useEffect(() => {
    fetchEquipos();
  }, []);

  const fetchEquipos = async () => {
    try {
      setLoading(true);
      const response = await api.get(endpoints.equipos);
      setEquipos(response.data.data || []);
    } catch (error) {
      setError('Error al cargar equipos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (mode, equipo = null) => {
    setDialogMode(mode);
    setSelectedEquipo(equipo);
    if (equipo) {
      setFormData({
        nombre: equipo.nombre || '',
        p96: equipo.p96 || 0,
        p118: equipo.p118 || 0,
        p64: equipo.p64 || 0,
        spec: equipo.spec || 0,
        bulk: equipo.bulk || 0,
        pyb: equipo.pyb || 0,
        pa: equipo.pa || 0,
        pqa: equipo.pqa || 0,
        tpos: equipo.tpos || 0,
        capcarga: equipo.capcarga || 0,
        maxload: equipo.maxload || 0,
        maxcol: equipo.maxcol || 13,
        nmfila: equipo.nmfila || 0,
      });
    } else {
      setFormData({
        nombre: '',
        p96: 0,
        p118: 0,
        p64: 0,
        spec: 0,
        bulk: 0,
        pyb: 0,
        pa: 0,
        pqa: 0,
        tpos: 0,
        capcarga: 0,
        maxload: 0,
        maxcol: 13,
        nmfila: 0,
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedEquipo(null);
    setError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'nombre' ? value : parseInt(value) || 0,
    }));
  };

  const calculateTotalPositions = () => {
    return formData.p96 + formData.p118 + formData.p64 + formData.spec + 
           formData.bulk + formData.pyb + formData.pa + formData.pqa;
  };

  const handleSubmit = async () => {
    if (!formData.nombre.trim()) {
      setError('El nombre es requerido');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const dataToSend = {
        ...formData,
        tpos: calculateTotalPositions(),
      };

      if (dialogMode === 'edit' && selectedEquipo) {
        await api.put(`${endpoints.equipos}?id=${selectedEquipo.id}`, dataToSend);
        setSuccess('Equipo actualizado exitosamente');
      } else {
        await api.post(endpoints.equipos, dataToSend);
        setSuccess('Equipo creado exitosamente');
      }

      handleCloseDialog();
      fetchEquipos();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (equipo) => {
    if (!confirm(`¿Eliminar el equipo "${equipo.nombre}"?`)) return;

    try {
      await api.delete(`${endpoints.equipos}?id=${equipo.id}`);
      setSuccess('Equipo eliminado');
      fetchEquipos();
    } catch (error) {
      setError(error.response?.data?.message || 'Error al eliminar');
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Alert severity="error">
          No tiene permisos para acceder a esta sección
        </Alert>
        <Button component={Link} to="/vuelos" sx={{ mt: 2 }}>
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #37474F 0%, #263238 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FlightIcon sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h4" fontWeight="bold">
                  Administrar Equipos
                </Typography>
                <Typography sx={{ opacity: 0.9 }}>
                  Configuración de aeronaves y posiciones de carga
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              Nuevo Equipo
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>ID</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell align="center">96"</TableCell>
                <TableCell align="center">118"</TableCell>
                <TableCell align="center">64"</TableCell>
                <TableCell align="center">SPEC</TableCell>
                <TableCell align="center">BULK</TableCell>
                <TableCell align="center">Total Pos</TableCell>
                <TableCell align="right">Cap. Carga (kg)</TableCell>
                <TableCell align="right">Max Load (kg)</TableCell>
                <TableCell align="center">Columnas</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : equipos.length > 0 ? (
                equipos.map((eq) => (
                  <TableRow key={eq.id} hover>
                    <TableCell>{eq.id}</TableCell>
                    <TableCell>
                      <Typography fontWeight="bold">{eq.nombre}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={eq.p96} size="small" color={eq.p96 > 0 ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={eq.p118} size="small" color={eq.p118 > 0 ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={eq.p64} size="small" color={eq.p64 > 0 ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell align="center">{eq.spec}</TableCell>
                    <TableCell align="center">{eq.bulk}</TableCell>
                    <TableCell align="center">
                      <Chip label={eq.tpos} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right">{(eq.capcarga || 0).toLocaleString()}</TableCell>
                    <TableCell align="right">{(eq.maxload || 0).toLocaleString()}</TableCell>
                    <TableCell align="center">{eq.maxcol}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog('edit', eq)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(eq)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                    <FlightIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                    <Typography color="text.secondary">No hay equipos registrados</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Actions */}
      <Box sx={{ mt: 3 }}>
        <Button component={Link} to="/admin" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>

      {/* Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FlightIcon color="primary" />
          {dialogMode === 'edit' ? `Editar Equipo: ${selectedEquipo?.nombre}` : 'Nuevo Equipo'}
          <IconButton
            onClick={handleCloseDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {/* Información básica */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                Información Básica
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre del Equipo"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                fullWidth
                required
                placeholder="Ej: 747, A-330, 767-300"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Columnas (maxcol)"
                name="maxcol"
                type="number"
                value={formData.maxcol}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
         

            {/* Posiciones */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                Configuración de Posiciones
              </Typography>
            </Grid>
            {positionFields.map((field) => (
              <Grid item xs={6} sm={4} md={3} key={field.name}>
                <TextField
                  label={field.label}
                  name={field.name}
                  type="number"
                  value={formData[field.name]}
                  onChange={handleInputChange}
                  fullWidth
                  helperText={field.description}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="Total Posiciones"
                value={calculateTotalPositions()}
                fullWidth
                disabled
                sx={{ '& .MuiInputBase-input': { fontWeight: 'bold' } }}
              />
            </Grid>

            {/* Capacidad */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                Capacidad de Carga
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Capacidad de Carga (kg)"
                name="capcarga"
                type="number"
                value={formData.capcarga}
                onChange={handleInputChange}
                fullWidth
                helperText="Peso máximo de carga en kilos"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Max Load (kg)"
                name="maxload"
                type="number"
                value={formData.maxload}
                onChange={handleInputChange}
                fullWidth
                helperText="Carga máxima permitida"
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Equipos;







